import { z } from 'zod';
import { BIOMES, type BiomeId } from '../../content/biomes';
import { MOWER_IDS, type MowerId } from '../../content/mowers';
import { CRITTER_KINDS, type CritterKind } from '../../sim/critters';

/** The pickups a lawn may have (rare finds and treasure belong to the campaign). */
export const BUILDER_PICKUPS = ['coin', 'gas', 'repair'] as const;
export type BuilderPickup = (typeof BUILDER_PICKUPS)[number];

/** Critters a lawn may have (sheep need a boss's pen). */
export const BUILDER_CRITTERS: readonly CritterKind[] = CRITTER_KINDS.filter((k) => k !== 'sheep');

/** A lawn builder lawn as the save keeps it (see meta/customLawns.ts). */
export const CustomLawnSchema = z.object({
  id: z.string().regex(/^my-\d+$/),
  name: z.string().min(1).max(24),
  /** The yard it sits in: the surroundings and the time of day. */
  biome: z.enum(BIOMES.map((b) => b.id) as [BiomeId, ...BiomeId[]]),
  /** Map rows (content/asciiMap.ts legend), with one `S`. */
  rows: z.array(z.string().min(1)).min(1),
  startDir: z.enum(['N', 'E', 'S', 'W']),
  pickups: z.array(
    z.object({ kind: z.enum(BUILDER_PICKUPS), x: z.number().int(), y: z.number().int() }),
  ),
  critters: z.array(
    z.object({
      kind: z.enum(BUILDER_CRITTERS as [CritterKind, ...CritterKind[]]),
      x: z.number().int(),
      y: z.number().int(),
    }),
  ),
  /** Set once checked: par per mower that clears it cleanly (cleared by any edit). */
  par: z.partialRecord(z.enum(MOWER_IDS as [MowerId, ...MowerId[]]), z.number().int()).optional(),
});
export type CustomLawn = z.infer<typeof CustomLawnSchema>;
